import type { Company } from "@repo/app-types/entities/company";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Modal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { IconShieldCheck } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { useUpdateCompanyMutation } from "../../../api/entities/company.js";
import {
  DEFAULT_REFILL_AMOUNT_OPTION,
  DEFAULT_REFILL_THRESHOLD_OPTION,
  NO_BALANCE_REFILL_OPTION,
  REFILL_AMOUNT_OPTIONS,
  REFILL_AMOUNT_OPTION_TO_VALUE,
  REFILL_THRESHOLD_OPTIONS,
  REFILL_THRESHOLD_OPTION_TO_VALUE,
  findUnitAmountOption,
} from "../../../helpers/transactionBalanceHelpers.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";

interface AutoRefillModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company;
}

export const AutoRefillModal: FC<AutoRefillModalProps> = ({
  isOpen,
  onClose,
  company,
}) => {
  const dispatch = useAppDispatch();
  const [updateCompany, { isLoading: isUpdateCompanyLoading }] =
    useUpdateCompanyMutation();

  const { balance_refill_unit_amount, balance_refill_threshold_unit_amount } =
    company.settings;

  // Set the initial value for the refill amount
  const refillAmountInitialValue =
    (balance_refill_unit_amount &&
      findUnitAmountOption(
        balance_refill_unit_amount || 0,
        REFILL_AMOUNT_OPTION_TO_VALUE,
      )) ||
    NO_BALANCE_REFILL_OPTION;
  const [balanceRefillAmount, setBalanceFillAmount] = useState<string>(
    refillAmountInitialValue,
  );

  // Set the initial value for the refill threshold
  const refillThresholdInitialValue =
    (balance_refill_threshold_unit_amount &&
      findUnitAmountOption(
        balance_refill_threshold_unit_amount,
        REFILL_THRESHOLD_OPTION_TO_VALUE,
      )) ||
    DEFAULT_REFILL_THRESHOLD_OPTION;
  const [balanceThresholdAmount, setBalanceThresholdAmount] = useState<string>(
    refillThresholdInitialValue,
  );

  const isRefillDisabled = balanceRefillAmount === NO_BALANCE_REFILL_OPTION;

  const onClickSubmit = async () => {
    try {
      await updateCompany({
        settings: {
          balance_refill_unit_amount: isRefillDisabled
            ? 0
            : (REFILL_AMOUNT_OPTION_TO_VALUE[balanceRefillAmount] ?? 0) * 100, // Convert to unit_amount (cents)
          balance_refill_threshold_unit_amount: isRefillDisabled
            ? null
            : (REFILL_THRESHOLD_OPTION_TO_VALUE[balanceThresholdAmount] ?? 0) *
              100,
        },
      }).unwrap();

      showNotification(dispatch, {
        id: `balance-refill-${new Date().getTime()}`,
        primaryMessage: `Auto-refill preferences updated`,
        type: NotificationType.SUCCESS,
      });

      onClose();
    } catch (err) {
      showNotification(dispatch, {
        id: `balance-refill-error-${new Date().getTime()}`,
        primaryMessage: `Sorry, an error occurred. Please try again.`,
        type: NotificationType.FAILURE,
      });

      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader onClose={onClose}>Auto-refill preferences</ModalHeader>

      <div className="mt-4 flex flex-col space-y-4">
        <div className="flex flex-row items-end space-x-4">
          <div className="w-[150px]">
            <SimpleSelect
              label="Refill amount (USD)"
              onChange={setBalanceFillAmount}
              options={REFILL_AMOUNT_OPTIONS}
              value={balanceRefillAmount}
            />
          </div>
          {isRefillDisabled ? (
            <div>
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => {
                  setBalanceFillAmount(DEFAULT_REFILL_AMOUNT_OPTION);
                  setBalanceThresholdAmount(DEFAULT_REFILL_THRESHOLD_OPTION);
                }}
              >
                Enable auto-refill
              </Button>
            </div>
          ) : (
            <div className="w-[150px]">
              <SimpleSelect
                label="Refill threshold (USD)"
                onChange={setBalanceThresholdAmount}
                options={REFILL_THRESHOLD_OPTIONS}
                value={balanceThresholdAmount}
              />
            </div>
          )}
        </div>
        {isRefillDisabled ? (
          <InfoBanner
            title="Auto-refill disabled"
            description={
              <>
                {`Disabling auto-refill can cause a poor customer experience if users get blocked from completing interviews due to an insufficient balance. Instead of disabling auto-refill, consider using the "Monthly interview limit" Project setting.`}
              </>
            }
            variant={SimpleModalVariant.Warning}
          />
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              {`When gift cards are being sent and the balance reaches ${balanceThresholdAmount} or less, ${balanceRefillAmount} will be added and the billing contact will be notified through an email invoice.`}
            </div>
            <InfoBanner
              title="Balance protection built-in"
              description={
                <>
                  {`Each completed interview is human-reviewed for authenticity
            before a gift card is sent using your balance. You can also cap the number of interviews you receive using the "Monthly interview limit" Project setting.`}
                </>
              }
              icon={<IconShieldCheck className="h-5 w-5 text-blue-400" />}
            />
          </>
        )}
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            variant={
              isRefillDisabled ? ButtonVariant.Warning : ButtonVariant.Primary
            }
            isDisabled={isUpdateCompanyLoading}
            onClick={() => onClickSubmit()}
            isLoading={isUpdateCompanyLoading}
          >
            Save preferences
          </Button>
        </div>
        <Button variant={ButtonVariant.Secondary} onClick={() => onClose()}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};
